/**
 * Table Component State Schema
 * 
 * Comprehensive table component state schema supporting editing,
 * validation, sync states, and advanced table features.
 * 
 * @module tableComponentStateSchema
 */

/**
 * Base table state schema
 */
const baseTableComponentStateSchema = {
  title: 'Table Component State Schema',
  type: 'object',
  required: ['isLoading', 'sorting', 'filters', 'rows', 'pagination', 'totalItems'],
  properties: {
    isLoading: {
      type: 'boolean',
      default: false,
      description: 'Indicates if the table is currently loading data'
    },
    sorting: {
      type: 'object',
      default: { sortBy: null, sortDirection: null },
      properties: {
        sortBy: {
          type: ['string', 'null'],
          default: null,
          description: 'Column ID to sort by'
        },
        sortDirection: {
          type: ['string', 'null'],
          enum: ['asc', 'desc', null],
          default: null,
          description: 'Sort direction'
        }
      }
    },
    filters: {
      type: 'array',
      default: [],
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          value: { type: 'any' }
        }
      },
      description: 'Array of active filters'
    },
    rows: {
      type: 'object',
      default: { all: [], filtered: [], selected: {}, viewable: [] },
      properties: {
        all: {
          type: 'array',
          default: [],
          items: { type: 'object' },
          description: 'All rows in the table'
        },
        filtered: {
          type: 'array',
          default: [],
          items: { type: 'object' },
          description: 'Filtered rows based on current filters'
        },
        selected: {
          type: 'object',
          default: {},
          description: 'Selected rows (rowId: boolean)'
        },
        viewable: {
          type: 'array',
          default: [],
          items: { type: 'object' },
          description: 'Rows currently viewable in the table'
        }
      }
    },
    pagination: {
      type: 'object',
      default: { pageSize: 25, pageIndex: 0 },
      properties: {
        pageSize: {
          type: 'number',
          default: 25,
          minimum: 1,
          description: 'Number of items per page'
        },
        pageIndex: {
          type: 'number',
          default: 0,
          minimum: 0,
          description: 'Current page index (0-based)'
        }
      }
    },
    totalItems: {
      type: 'number',
      default: 0,
      minimum: 0,
      description: 'Total number of items across all pages'
    },
    actionsButton: {
      type: 'object',
      default: {
        menu: {
          isMenuOpen: false,
          activeSubmenuId: null,
          showSubmenuOnLeft: false,
          menuOptions: []
        }
      },
      properties: {
        menu: {
          type: 'object',
          default: {
            isMenuOpen: false,
            activeSubmenuId: null,
            showSubmenuOnLeft: false,
            menuOptions: []
          },
          properties: {
            isMenuOpen: {
              type: 'boolean',
              default: false,
              description: 'Whether the actions menu is open'
            },
            activeSubmenuId: {
              type: ['string', 'null'],
              default: null,
              description: 'ID of the currently active submenu'
            },
            showSubmenuOnLeft: {
              type: 'boolean',
              default: false,
              description: 'Whether to show submenu on the left side'
            },
            menuOptions: {
              type: 'array',
              default: [],
              items: { type: 'object' },
              description: 'Available menu options'
            }
          }
        }
      }
    },
    rowActions: {
      type: 'object',
      default: {},
      description: 'Row-specific action states (rowId: actionStates)',
      additionalProperties: {
        type: 'object',
        properties: {
          isLoading: {
            type: 'boolean',
            default: false,
            description: 'Indicates if the row is currently loading.'
          },
          isDisabled: {
            type: 'boolean',
            default: false,
            description: 'Indicates if the row actions are disabled.'
          },
          isHovered: {
            type: 'boolean',
            default: false,
            description: 'Indicates if the row is currently hovered.'
          },
          isSelected: {
            type: 'boolean',
            default: false,
            description: 'Indicates if the row is currently selected.'
          }
        }
      }
    }
  }
};

/**
 * Enhanced table state schema with editing and validation support
 */
export const tableComponentStateSchema = {
  ...baseTableComponentStateSchema,
  properties: {
    ...baseTableComponentStateSchema.properties,
    
    // Enhanced editing state
    editing: {
      type: 'object',
      default: {},
      description: 'Comprehensive editing state management',
      properties: {
        activeCell: {
          type: ['object', 'null'],
          default: null,
          description: 'Currently active edit session',
          properties: {
            rowId: { 
              type: 'string',
              description: 'ID of the row being edited'
            },
            columnId: { 
              type: 'string',
              description: 'ID of the column being edited'
            },
            originalValue: { 
              type: 'any',
              description: 'Original value before editing'
            },
            currentValue: { 
              type: 'any',
              description: 'Current edit value'
            },
            isValidating: { 
              type: 'boolean', 
              default: false,
              description: 'Whether validation is currently running'
            }
          }
        },
        validationErrors: {
          type: 'object',
          default: {},
          description: 'Validation errors keyed by "rowId.columnId"',
          additionalProperties: {
            type: 'string'
          }
        },
        optimisticUpdates: {
          type: 'object',
          default: {},
          description: 'Optimistic updates keyed by "rowId.columnId"',
          additionalProperties: {
            type: 'object',
            properties: {
              value: { type: 'any', description: 'Optimistic value' },
              timestamp: { type: 'number', description: 'When the update was made' }
            }
          }
        },
        pendingUpdates: {
          type: 'array',
          default: [],
          items: { type: 'string' },
          description: 'List of "rowId.columnId" currently being updated'
        },
        editHistory: {
          type: 'array',
          default: [],
          description: 'History of edit operations for undo/redo',
          items: {
            type: 'object',
            properties: {
              id: { type: 'string', description: 'Unique operation ID' },
              timestamp: { type: 'number', description: 'When operation occurred' },
              type: { 
                type: 'string', 
                enum: ['cell-edit', 'bulk-edit', 'row-delete', 'row-create'],
                description: 'Type of operation'
              },
              action: { 
                type: 'object',
                description: 'Action that was performed'
              },
              inverse: { 
                type: 'object',
                description: 'Inverse action for undo'
              }
            }
          }
        },
        undoStack: {
          type: 'array',
          default: [],
          items: { type: 'object' },
          description: 'Stack of actions that can be undone'
        },
        redoStack: {
          type: 'array',
          default: [],
          items: { type: 'object' },
          description: 'Stack of actions that can be redone'
        },
        maxHistorySize: {
          type: 'number',
          default: 50,
          description: 'Maximum number of operations to keep in history'
        }
      }
    },
    
    // Enhanced sync state
    syncState: {
      type: 'object',
      default: {},
      description: 'Comprehensive sync state management',
      properties: {
        syncingItems: {
          type: 'array',
          default: [],
          items: { type: 'string' },
          description: 'List of item IDs currently being synced'
        },
        isBulkOperation: {
          type: 'boolean',
          default: false,
          description: 'Whether a bulk operation is in progress'
        },
        operationType: {
          type: ['string', 'null'],
          default: null,
          enum: ['sync', 'update', 'delete', 'create', 'bulk-update', 'bulk-delete', null],
          description: 'Type of operation currently in progress'
        },
        syncProgress: {
          type: 'object',
          default: {},
          description: 'Progress tracking for long operations',
          properties: {
            total: { type: 'number', default: 0 },
            completed: { type: 'number', default: 0 },
            failed: { type: 'number', default: 0 },
            currentItem: { type: 'string', default: null }
          }
        },
        lastSync: {
          type: ['number', 'null'],
          default: null,
          description: 'Timestamp of last successful sync'
        }
      }
    },
    
    // Enhanced error handling
    errors: {
      type: 'object',
      default: {},
      description: 'Comprehensive error state management',
      properties: {
        load: { 
          type: ['string', 'null'], 
          default: null,
          description: 'Error loading table data'
        },
        update: { 
          type: ['string', 'null'], 
          default: null,
          description: 'Error updating row data'
        },
        validation: { 
          type: ['string', 'null'], 
          default: null,
          description: 'Validation error'
        },
        sync: { 
          type: ['string', 'null'], 
          default: null,
          description: 'Sync operation error'
        },
        bulk: { 
          type: ['string', 'null'], 
          default: null,
          description: 'Bulk operation error'
        },
        network: { 
          type: ['string', 'null'], 
          default: null,
          description: 'Network-related error'
        }
      }
    },

    // Bulk operations state
    bulkOperations: {
      type: 'object',
      default: {},
      description: 'State for bulk edit and operations',
      properties: {
        isSelectionMode: {
          type: 'boolean',
          default: false,
          description: 'Whether bulk selection mode is active'
        },
        selectedForBulkEdit: {
          type: 'object',
          default: {},
          description: 'Rows selected for bulk editing (rowId: boolean)'
        },
        bulkEditValues: {
          type: 'object',
          default: {},
          description: 'Values to apply in bulk edit (columnId: value)'
        },
        bulkEditMode: {
          type: 'boolean',
          default: false,
          description: 'Whether bulk edit form is active'
        }
      }
    },

    // Bulk operation dialog state
    bulkOperationDialog: {
      type: 'object',
      default: {},
      description: 'State for bulk operation loading and results dialog',
      properties: {
        isOpen: {
          type: 'boolean',
          default: false,
          description: 'Whether the bulk operation dialog is visible'
        },
        isProcessing: {
          type: 'boolean',
          default: false,
          description: 'Whether a bulk operation is currently processing'
        },
        processingMessage: {
          type: 'string',
          default: '',
          description: 'Message to display while processing (e.g., "Marking 5 items as listed. One moment")'
        },
        itemCount: {
          type: 'number',
          default: 0,
          minimum: 0,
          description: 'Total number of items being processed'
        },
        actionLabel: {
          type: 'string',
          default: '',
          description: 'Label describing the action being performed'
        },
        entityLabel: {
          type: 'string',
          default: '',
          description: 'Label describing the type of entity being processed'
        },
        results: {
          type: 'object',
          default: {},
          description: 'Results of the bulk operation',
          properties: {
            successful: {
              type: 'number',
              default: 0,
              minimum: 0,
              description: 'Number of items successfully processed'
            },
            failed: {
              type: 'number',
              default: 0,
              minimum: 0,
              description: 'Number of items that failed'
            },
            skipped: {
              type: 'number',
              default: 0,
              minimum: 0,
              description: 'Number of items that were skipped'
            },
            errorMessage: {
              type: ['string', 'null'],
              default: null,
              description: 'Error message if operation failed'
            }
          }
        },
        fullResults: {
          type: 'object',
          default: null,
          description: 'Full results arrays with item details for expandable display',
          properties: {
            successful: {
              type: 'array',
              default: [],
              items: { type: 'object' },
              description: 'Array of successfully processed items'
            },
            failed: {
              type: 'array',
              default: [],
              items: { type: 'object' },
              description: 'Array of failed items with error details'
            },
            skipped: {
              type: 'array',
              default: [],
              items: { type: 'object' },
              description: 'Array of skipped items with reasons'
            }
          }
        },
        fieldConfig: {
          type: ['object', 'null'],
          default: null,
          description: 'Field configuration for expandable result sections',
          properties: {
            failed: {
              type: 'object',
              description: 'Field config for failed items display',
              properties: {
                fields: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      key: { type: 'string', description: 'Dot notation path to value' },
                      label: { type: 'string', description: 'Display label' }
                    }
                  }
                }
              }
            },
            skipped: {
              type: 'object',
              description: 'Field config for skipped items display',
              properties: {
                fields: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      key: { type: 'string', description: 'Dot notation path to value' },
                      label: { type: 'string', description: 'Display label' }
                    }
                  }
                }
              }
            }
          }
        }
      }
    },

    // Performance and UI state
    performance: {
      type: 'object',
      default: {},
      description: 'Performance and UI optimization state',
      properties: {
        virtualization: {
          type: 'object',
          default: {},
          properties: {
            enabled: { type: 'boolean', default: false },
            rowHeight: { type: 'number', default: 50 },
            overscan: { type: 'number', default: 5 }
          }
        },
        debounceSettings: {
          type: 'object',
          default: {},
          properties: {
            editCommit: { type: 'number', default: 300 },
            validation: { type: 'number', default: 300 },
            search: { type: 'number', default: 300 }
          }
        }
      }
    },

    // Enhanced row actions (extends base rowActions)
    rowActions: {
      ...baseTableComponentStateSchema.properties.rowActions,
      properties: {
        ...baseTableComponentStateSchema.properties.rowActions.properties,
        
        // Edit-specific actions
        isEditing: { 
          type: 'boolean', 
          default: false,
          description: 'Indicates if the row is currently being edited.'
        },
        hasValidationErrors: { 
          type: 'boolean',
          default: false,
          description: 'Indicates if the row has validation errors.'
        },
        hasOptimisticUpdates: { 
          type: 'boolean',
          default: false,
          description: 'Indicates if the row has optimistic updates pending.'
        },
        
        // Sync-specific actions
        isSyncing: { 
          type: 'boolean',
          default: false,
          description: 'Indicates if the row is currently being synced.'
        },
        syncProgress: { 
          type: 'number',
          minimum: 0,
          maximum: 100,
          default: 0,
          description: 'Sync progress percentage (0-100).'
        },
        lastSyncTime: { 
          type: ['number', 'null'],
          default: null,
          description: 'Timestamp of last successful sync for this row.'
        },
        
        // Bulk operation actions
        isSelectedForBulk: { 
          type: 'boolean',
          default: false,
          description: 'Indicates if the row is selected for bulk operations.'
        }
      }
    }
  }
};

/**
 * Helper function to merge page-specific schemas with table schema
 * 
 * @param {Object} pageRowSchema - Page-specific row structure schema
 * @param {Object} pageRowActions - Page-specific row action states
 * @param {Object} pageExtensions - Additional page-specific table state properties
 * @returns {Object} Complete page table schema
 */
export const createPageTableSchema = (pageRowSchema = {}, pageRowActions = {}, pageExtensions = {}) => {
  // Deep copy the table schema to avoid modifying the original. The schema is plain
  // JSON; structuredClone is avoided because Safari before 15.4 lacks it and this
  // runs at module load, which left the whole app blank on older iPads.
  const pageTableSchema = JSON.parse(JSON.stringify(tableComponentStateSchema));

  // Define the specific item structure for rows
  pageTableSchema.properties.rows.properties.all.items = {
    ...pageTableSchema.properties.rows.properties.all.items,
    ...pageRowSchema
  };
  pageTableSchema.properties.rows.properties.filtered.items = {
    ...pageTableSchema.properties.rows.properties.filtered.items,
    ...pageRowSchema
  };
  pageTableSchema.properties.rows.properties.selected.items = {
    ...pageTableSchema.properties.rows.properties.selected.items,
    ...pageRowSchema
  };
  pageTableSchema.properties.rows.properties.viewable.items = {
    ...pageTableSchema.properties.rows.properties.viewable.items,
    ...pageRowSchema
  };

  // Merge page-specific row action states
  pageTableSchema.properties.rowActions.properties = {
    ...pageTableSchema.properties.rowActions.properties,
    ...pageRowActions
  };

  // Add any additional page-specific properties
  if (pageExtensions && Object.keys(pageExtensions).length > 0) {
    pageTableSchema.properties = {
      ...pageTableSchema.properties,
      ...pageExtensions
    };
  }

  return pageTableSchema;
};

/**
 * Helper function to create initial table state
 * 
 * @param {Object} customDefaults - Custom default values
 * @returns {Object} Initial table state with all features
 */
export const createInitialTableState = (customDefaults = {}) => {
  return {
    isLoading: false,
    sorting: {
      sortBy: null,
      sortDirection: null
    },
    filters: [],
    rows: {
      all: [],
      filtered: [],
      selected: {},
      viewable: []
    },
    pagination: {
      pageSize: 25,
      pageIndex: 0
    },
    totalItems: 0,
    actionsButton: {
      menu: {
        isMenuOpen: false,
        activeSubmenuId: null,
        showSubmenuOnLeft: false,
        menuOptions: []
      }
    },
    editing: {
      activeCell: null,
      validationErrors: {},
      optimisticUpdates: {},
      pendingUpdates: [],
      editHistory: [],
      undoStack: [],
      redoStack: [],
      maxHistorySize: 50
    },
    syncState: {
      syncingItems: [],
      isBulkOperation: false,
      operationType: null,
      syncProgress: {
        total: 0,
        completed: 0,
        failed: 0,
        currentItem: null
      },
      lastSync: null
    },
    errors: {
      load: null,
      update: null,
      validation: null,
      sync: null,
      bulk: null,
      network: null
    },
    bulkOperations: {
      isSelectionMode: false,
      selectedForBulkEdit: {},
      bulkEditValues: {},
      bulkEditMode: false
    },
    bulkOperationDialog: {
      isOpen: false,
      isProcessing: false,
      processingMessage: '',
      itemCount: 0,
      actionLabel: '',
      entityLabel: '',
      results: {
        successful: 0,
        failed: 0,
        skipped: 0,
        errorMessage: null
      },
      fullResults: null,
      fieldConfig: null
    },
    performance: {
      virtualization: {
        enabled: false,
        rowHeight: 50,
        overscan: 5
      },
      debounceSettings: {
        editCommit: 300,
        validation: 300,
        search: 300
      }
    },
    rowActions: {},
    ...customDefaults
  };
};

/**
 * State migration helper for upgrading existing table state to current schema
 * 
 * @param {Object} existingState - Current table state
 * @returns {Object} Migrated table state
 */
export const migrateToTableState = (existingState = {}) => {
  const initialState = createInitialTableState();
  
  // Merge existing state, preserving all current data
  return {
    ...initialState,
    ...existingState,
    // Ensure new properties exist even if not in existing state
    editing: {
      ...initialState.editing,
      ...existingState.editing
    },
    syncState: {
      ...initialState.syncState,
      ...existingState.syncState
    },
    errors: {
      ...initialState.errors,
      ...existingState.errors
    },
    bulkOperations: {
      ...initialState.bulkOperations,
      ...existingState.bulkOperations
    },
    bulkOperationDialog: {
      ...initialState.bulkOperationDialog,
      ...existingState.bulkOperationDialog
    },
    performance: {
      ...initialState.performance,
      ...existingState.performance
    }
  };
};

export default tableComponentStateSchema;
